import React from 'react';

const Chapter7Content = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Chapter Seven</h1>
        <p className="text-xl text-gray-600">Why We Missed It</p>
        <p className="text-lg text-gray-500 mt-2">The Gorilla in the Text</p>
      </div>

      {/* The Gorilla Experiment */}
      <div className="mb-12">
        <p className="text-gray-800 leading-relaxed mb-4">
          In 1999, two researchers at Harvard University made a short video that changed how we understand human attention.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          They filmed people passing basketballs and asked their viewers to count how many times the team in white passed the ball. The subjects focused, counted carefully, and did their best.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Halfway through the video, a person in a full gorilla suit walked into the middle of the scene. The gorilla stopped. Looked at the camera. Beat his chest for nine full seconds. And walked off.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          When the video ended, the researchers asked: <em>Did you see the gorilla?</em>
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          <strong>More than half the subjects said: What gorilla?</strong>
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          They had not been careless. They had been intensely focused. But the counting task had consumed every bit of their attention. And so the gorilla — enormous, obvious, right in the middle of the scene — walked through completely unseen.
        </p>
      </div>

      <div className="bg-red-50 border-l-4 border-red-600 p-6 my-8">
        <h2 className="text-2xl font-bold text-red-900 mb-4">Something very similar happened with the Bible.</h2>
      </div>

      {/* The Counting Task */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">The Counting Task</h2>
        <p className="text-gray-800 leading-relaxed mb-4">
          Sincere, gifted, devoted teachers of Scripture were given a counting task by the theological tradition they inherited. Count the dispensations. Maintain the Israel-Church distinction. Locate the pre-tribulation rapture in the text. Find the gap.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          And while they counted — faithfully, carefully, with deep love for God's Word:
        </p>
        
        <div className="bg-gray-100 border-l-4 border-gray-400 p-6 my-6 space-y-3">
          <p className="text-gray-900">
            The gorilla walked through Matthew 24. <em>"Immediately after the tribulation... they gather His elect."</em>
          </p>
          <p className="text-gray-900">
            The gorilla walked through 1 Corinthians 15. <em>"At the last trumpet."</em>
          </p>
          <p className="text-gray-900">
            The gorilla walked through Revelation 20. <em>"This is the first resurrection."</em>
          </p>
          <p className="text-gray-900">
            The gorilla walked through Revelation 16. Jesus saying <em>"I come as a thief"</em> — inside Armageddon.
          </p>
        </div>
      </div>

      {/* What Was Taken */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">What Was Taken</h2>
        <p className="text-gray-800 leading-relaxed mb-4">
          The teachers who taught theories of the rapture were not wicked people. Most of them loved God with everything they had. They were doing what all of us do — receiving what we were taught, trusting those who taught us, and passing it forward in good faith.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          But good faith does not make something true. Theories are opinions. And the text has been waiting, patiently, for anyone willing to stop counting tasks long enough to see the gorilla.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          The legend was buried under complexity. The treasure map was covered by tradition. We are uncovering both.
        </p>
      </div>

      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-8 text-center my-12">
        <p className="text-xl font-bold text-gray-900 mb-4">
          The doctrine that removed you from Matthew 24 was sincere.
        </p>
        <p className="text-lg text-gray-800 mb-4">
          The theft was not malicious — it was the slow accumulation of a counting task that trained generations not to see the gorilla.
        </p>
        <p className="text-lg text-gray-800">
          But what was taken was real: your place in the text. Your name in the story. Your seat at the table of endurance and overcoming and the triumphant, visible, trumpet-sounding, resurrection-completing, Antichrist-destroying return of the King of Kings.
        </p>
      </div>

      <div className="text-center my-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          The grammar has given it back.
        </h2>
        <p className="text-xl text-gray-700">
          Come home to the text.
        </p>
      </div>

      <div className="flex justify-center gap-4 my-8">
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
      </div>
    </article>
  );
};

export default Chapter7Content;